import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { dashboardAPI } from '../utils/api';
import { LogOut, TrendingUp, Clock, AlertTriangle, Users, Settings, RefreshCw } from 'lucide-react';
import KeywordsManagement from '../components/KeywordsManagement';
import SystemSettings from '../components/SystemSettings';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ManagerDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchMetrics();
    const interval = setInterval(fetchMetrics, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const fetchMetrics = async () => {
    try {
      const response = await dashboardAPI.getMetrics();
      setMetrics(response.data);
    } catch (error) {
      console.error('Error fetching metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRecalculatePriorities = async () => {
    if (!window.confirm('Recalculate AI priority for all existing pages? This will update priorities based on the new algorithm.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/admin/recalculate-priorities`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert('✅ Priority recalculation started! Check Render logs for progress. Refresh the page in 30 seconds to see updated priorities.');
      
      // Refresh metrics after 30 seconds
      setTimeout(() => {
        fetchMetrics();
      }, 30000);
    } catch (error) {
      console.error('Error recalculating priorities:', error);
      alert('❌ Failed to start recalculation: ' + (error.response?.data?.error || error.message));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-2 sm:gap-4">
              <img src="/REACH_LOGO.png" alt="Reach Logo" className="h-10 sm:h-12 w-auto bg-transparent" />
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Management Dashboard</h1>
                <p className="text-xs sm:text-sm text-gray-600">Welcome, {user?.name}</p>
              </div>
            </div>
            <button
              onClick={logout}
              className="flex items-center gap-1 sm:gap-2 bg-gray-200 text-gray-700 px-3 sm:px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors text-sm sm:text-base w-full sm:w-auto justify-center"
            >
              <LogOut className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="hidden sm:inline">Logout</span>
              <span className="sm:hidden">Out</span>
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Overview
              </div>
            </button>
            <button
              onClick={() => setActiveTab('keywords')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'keywords'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Keywords Management
              </div>
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'settings'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                System Settings
              </div>
            </button>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8 space-y-6">
        {activeTab === 'keywords' ? (
          <KeywordsManagement />
        ) : activeTab === 'settings' ? (
          <SystemSettings />
        ) : (
          <>
        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">Total Tasks</p>
              <TrendingUp className="w-5 h-5 text-blue-600" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{metrics?.overview.total || 0}</p>
            <p className="text-sm text-gray-600 mt-1">Last 24h: {metrics?.overview.last24Hours || 0}</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">Avg Response Time</p>
              <Clock className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{metrics?.responseTime.average || 0}</p>
            <p className="text-sm text-gray-600 mt-1">minutes</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">EHR Escalations</p>
              <AlertTriangle className="w-5 h-5 text-orange-600" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{metrics?.ehrEscalations.total || 0}</p>
            <p className="text-sm text-gray-600 mt-1">{metrics?.ehrEscalations.percentage || 0}% of total</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">Completion Rate</p>
              <Users className="w-5 h-5 text-purple-600" />
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {metrics?.overview.byStatus.completed || 0}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              {metrics?.overview.total > 0 
                ? Math.round((metrics.overview.byStatus.completed / metrics.overview.total) * 100)
                : 0}% completed
            </p>
          </div>
        </div>

        {/* Tasks by Status */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Tasks by Status</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-orange-600">
                {metrics?.overview.byStatus.pending || 0}
              </p>
              <p className="text-sm text-gray-600">Pending</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-yellow-600">
                {metrics?.overview.byStatus.acknowledged || 0}
              </p>
              <p className="text-sm text-gray-600">Acknowledged</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">
                {metrics?.overview.byStatus.inProgress || 0}
              </p>
              <p className="text-sm text-gray-600">In Progress</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">
                {metrics?.overview.byStatus.completed || 0}
              </p>
              <p className="text-sm text-gray-600">Completed</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-600">
                {metrics?.overview.byStatus.cancelled || 0}
              </p>
              <p className="text-sm text-gray-600">Cancelled</p>
            </div>
          </div>
        </div>

        {/* Tasks by Priority */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">Tasks by Priority (Eisenhower Matrix)</h2>
            <button
              onClick={handleRecalculatePriorities}
              className="flex items-center gap-1 sm:gap-2 bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-xs sm:text-sm w-full sm:w-auto justify-center"
            >
              <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Recalculate All Priorities</span>
              <span className="sm:hidden">Recalculate</span>
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-red-800">
                {metrics?.overview.byPriority.priority1 || 0}
              </p>
              <p className="text-sm text-red-700 mt-1">Priority 1</p>
              <p className="text-xs text-red-600">DO FIRST</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-blue-800">
                {metrics?.overview.byPriority.priority2 || 0}
              </p>
              <p className="text-sm text-blue-700 mt-1">Priority 2</p>
              <p className="text-xs text-blue-600">SCHEDULE</p>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-yellow-800">
                {metrics?.overview.byPriority.priority3 || 0}
              </p>
              <p className="text-sm text-yellow-700 mt-1">Priority 3</p>
              <p className="text-xs text-yellow-600">DELEGATE</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-gray-800">
                {metrics?.overview.byPriority.priority4 || 0}
              </p>
              <p className="text-sm text-gray-700 mt-1">Priority 4</p>
              <p className="text-xs text-gray-600">DEFER</p>
            </div>
          </div>
        </div>

        {/* EHR Escalation Reasons */}
        {metrics?.ehrEscalations.reasons && Object.keys(metrics.ehrEscalations.reasons).length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Top EHR Escalation Reasons</h2>
            <div className="space-y-3">
              {Object.entries(metrics.ehrEscalations.reasons)
                .sort(([, a], [, b]) => b - a)
                .slice(0, 5)
                .map(([reason, count]) => (
                  <div key={reason} className="flex items-center justify-between">
                    <p className="text-sm text-gray-700">{reason}</p>
                    <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-medium">
                      {count} tasks
                    </span>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Workload by Doctor */}
        {metrics?.workload && Object.keys(metrics.workload).length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Doctor Workload - Eisenhower Matrix Breakdown</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Doctor</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-red-600 uppercase" title="Urgent & Important">
                      Do First
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-blue-600 uppercase" title="Important, Not Urgent">
                      Schedule
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-yellow-600 uppercase" title="Urgent, Not Important">
                      Delegate
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase" title="Neither Urgent Nor Important">
                      Defer
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pending</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Completed</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Completion %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {Object.entries(metrics.workload).map(([doctorId, data]) => (
                    <tr key={doctorId}>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{doctorId}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900">{data.total}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          {data.priority1 || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {data.priority2 || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          {data.priority3 || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {data.priority4 || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-orange-600 font-medium">{data.pending}</td>
                      <td className="px-4 py-3 text-sm text-green-600 font-medium">{data.completed}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-green-600 h-2 rounded-full"
                              style={{ width: `${data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0}%` }}
                            ></div>
                          </div>
                          <span className="text-xs font-medium">
                            {data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Legend */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-500 mb-2 font-medium">Eisenhower Matrix Priority Levels:</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center px-2 py-1 rounded-full bg-red-100 text-red-800 font-medium">P1</span>
                  <span className="text-gray-600">Do First - Urgent & Important</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-100 text-blue-800 font-medium">P2</span>
                  <span className="text-gray-600">Schedule - Important, Not Urgent</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center px-2 py-1 rounded-full bg-yellow-100 text-yellow-800 font-medium">P3</span>
                  <span className="text-gray-600">Delegate - Urgent, Not Important</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-gray-800 font-medium">P4</span>
                  <span className="text-gray-600">Defer - Neither Urgent Nor Important</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Activity by Hour */}
        {metrics?.activityByHour && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Activity by Hour (Last 24h)</h2>
            <div className="flex items-end justify-between h-48 gap-1">
              {Object.entries(metrics.activityByHour).map(([hour, count]) => {
                const maxCount = Math.max(...Object.values(metrics.activityByHour));
                const height = maxCount > 0 ? (count / maxCount) * 100 : 0;
                return (
                  <div key={hour} className="flex-1 flex flex-col items-center">
                    <div 
                      className="w-full bg-blue-500 rounded-t transition-all hover:bg-blue-600"
                      style={{ height: `${height}%` }}
                      title={`${hour}:00 - ${count} tasks`}
                    ></div>
                    <p className="text-xs text-gray-600 mt-2">{hour}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        </>
        )}
      </main>
    </div>
  );
};

export default ManagerDashboard;
